import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setTableParams } from 'reducers/tableParams';
import { openModal } from 'reducers/modals';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
import { useDropdownActions } from 'hooks/useDropdownContext';
// endpoints
import {
  getManageTreatmentProtocols,
  updateFarmTreatmentProtocol,
  deleteFarmTreatmentProtocol,
  updatePigGroupTreatmentProtocol,
  deletePigGroupTreatmentProtocol,
  archivePigGroupTreatmentProtocol,
  bulkUpdatePigGroupTreatmentProtocols,
  bulkDeletePigGroupTreatmentProtocols,
} from 'endpoints/treatmentProtocols';
// components
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import DataTable from 'components/DataTable/DataTable';
import { CustomColumn } from 'components/DataTable/Columns';
import {
  CombinedBadge,
  ComplianceStatusBadge,
  TreatmentProtocolStatusBadge,
} from 'components/StatusBadge/StatusBadge';
import DropdownButton from 'components/DropdownButton/DropdownButton';
import Panel from 'components/Panel';
import SearchBox from 'components/SearchBox';
import Preloader from 'components/Preloader';
import NothingBox from 'components/NothingBox';
import TableFilter from 'components/TableFilter';
import Button from 'components/Button';
import DeleteTreatmentProtocolModal from './components/DeleteTreatmentProtocolModal';
import CreateTreatmentProtocolModal from './components/CreateTreatmentProtocolModal';
import CombinedTreatmentProtocolModal from './components/CombinedTreatmentProtocolModal';
import EffectiveDates from 'components/TreatmentProtocols/EffectiveDates';
// utils
import { tableNames } from 'utils/constants';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatDateRangeForUser } from 'utils/regionalDateHelper';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import cn from 'classnames';
// styles
import styles from './TreatmentProtocols.module.scss';
import './FarmsSetUp.scss';

const FarmsSetUp = ({
  router,
  tableParams,
  setTableParams,
  setPageOptions,
  openModal,
  setBreadcrumbs,
  countryCode,
}) => {
  const {
    data: {
      resources,
      isLoading,
      meta: { total, stats },
    },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    onSortChange,
    onStatusChange,
  } = useDataTable(getManageTreatmentProtocols, {
    tableParams,
    setTableParams: (params) => setTableParams(tableNames.manageTreatmentProtocols, params),
  });

  const { openDropdown } = useDropdownActions();
  const { page, per_page, search, sort, status } = tableParams;

  const paginationProps = {
    onPageChange,
    onPerPageChange,
    totalItems: total,
    currentPage: page,
    perPage: per_page,
  };

  const treatmentProtocolsTableFilters = [
    { label: <FormattedMessage id="general.status.active" />, value: 'active' },
    { label: <FormattedMessage id="general.activeNonCompliant" />, value: 'active_non_compliant' },
    { label: <FormattedMessage id="general.status.completed" />, value: 'completed' },
    { label: <FormattedMessage id="general.status.archived" />, value: 'archived' },
    { label: <FormattedMessage id="general.status.disabled" />, value: 'disabled' },
  ];

  useEffect(() => {
    fetchData({
      ...tableParams,
      sort: tableParams.sort || 'created_at desc',
      status: tableParams.status || 'active',
    }).catch(toastResponseErrors);
    setPageOptions({ backLink: '' });
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocols" />,
        path: '/treatment-protocols-farms',
        useLabelAsMobileTitle: true,
      },
    ]);
  }, []);

  const handleCreateProtocol = () => {
    openModal(
      <CreateTreatmentProtocolModal
        onContinue={(path) => router.push(path)}
      />
    );
  };

  const onFarmProtocolStatusChange = async (rowData, isActive, withGroups = false) => {
    try {
      const additionalData = withGroups ? { pg_protocols_update: true } : {};
      await updateFarmTreatmentProtocol(rowData.farm.id, rowData.id, { active: isActive }, additionalData);
      fetchData(tableParams);
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const getCopyIds = (rowData) => rowData.protocols.map(({ id }) => id);

  // A combined row stands for the copies of several sow units in one group; every action on it
  // explains first which copies it touches
  const openCombinedModal = (rowData, action, onConfirm, onEditCopy) => {
    openModal(
      <CombinedTreatmentProtocolModal
        action={action}
        treatmentProtocol={rowData}
        groupName={rowData.pig_group?.name}
        onConfirm={onConfirm}
        onEditCopy={onEditCopy}
      />
    );
  };

  const onGroupProtocolStatusChange = async (rowData, isActive) => {
    try {
      if (rowData.combined) {
        await bulkUpdatePigGroupTreatmentProtocols(rowData.pig_group.id, getCopyIds(rowData), { active: isActive });
      } else {
        await updatePigGroupTreatmentProtocol(rowData.pig_group.id, rowData.id, { active: isActive });
      }
      fetchData(tableParams);
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const handleGroupStatusChange = (rowData) => {
    const isActive = !rowData.active;
    if (rowData.combined) {
      const action = isActive ? 'enable' : 'disable';
      openCombinedModal(rowData, action, () => onGroupProtocolStatusChange(rowData, isActive));
      return;
    }
    onGroupProtocolStatusChange(rowData, isActive);
  };

  // A combined row archives the copies that did not comply; the complied ones keep their grade.
  // One by one, and the table reloads either way, so a failure never hides what was archived.
  const onArchiveGroupProtocol = async (rowData) => {
    const ids = rowData.combined
      ? rowData.protocols.filter(({ compliance }) => compliance === 'did_not_comply').map(({ id }) => id)
      : [rowData.id];
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await archivePigGroupTreatmentProtocol(rowData.pig_group.id, id);
      }
    } catch (error) {
      toastResponseErrors(error);
    }
    fetchData(tableParams);
  };

  const onDeleteFarmProtocol = async (rowData) => {
    try {
      await deleteFarmTreatmentProtocol(rowData.farm.id, rowData.id);
      fetchData(tableParams);
    } catch (err) {
      toastResponseErrors(err);
    }
  };

  const onDeleteGroupProtocol = async (rowData) => {
    try {
      if (rowData.combined) {
        await bulkDeletePigGroupTreatmentProtocols(rowData.pig_group.id, getCopyIds(rowData));
      } else {
        await deletePigGroupTreatmentProtocol(rowData.pig_group.id, rowData.id);
      }
      fetchData(tableParams);
    } catch (err) {
      toastResponseErrors(err);
    }
  };

  const handleDeleteProtocol = (rowData) => {
    const onDelete = rowData.source_type === 'farm'
      ? () => onDeleteFarmProtocol(rowData)
      : () => onDeleteGroupProtocol(rowData);

    if (rowData.combined) {
      openCombinedModal(rowData, 'delete', onDelete);
      return;
    }

    openModal(
      <DeleteTreatmentProtocolModal
        treatmentProtocol={rowData}
        onDeleteTreatmentProtocol={onDelete}
      />
    );
  };

  const getFarmEditPath = (rowData) => (
    `/treatment-protocols-farms/${rowData.farm.id}/treatment-protocols/${rowData.id}/edit`
  );

  const getGroupEditPath = (rowData, protocolId = rowData.id) => (
    `/treatment-protocols-farms/${rowData.farm.id}/groups/${rowData.pig_group.id}` +
    `/treatment-protocols/${protocolId}/edit`
  );

  // Identical copies are edited together, copies that differ one by one
  const handleGroupEdit = (rowData) => {
    if (!rowData.combined) {
      router.push(getGroupEditPath(rowData));
      return;
    }
    openCombinedModal(
      rowData,
      'edit',
      () => router.push(`${getGroupEditPath(rowData)}?sync_combined=true`),
      (copy) => router.push(getGroupEditPath(rowData, copy.id)),
    );
  };

  const buildFarmOptions = (rowData) => [
    {
      label: <FormattedMessage id="general.button.edit" />,
      onClick: () => router.push(getFarmEditPath(rowData)),
    },
    {
      label: <FormattedMessage id={`general.button.${rowData.active ? 'disableForFarm' : 'enableForFarm'}`} />,
      onClick: () => onFarmProtocolStatusChange(rowData, !rowData.active),
    },
    {
      label: (
        <FormattedMessage
          id={`general.button.${rowData.active ? 'disableForFarmAndGroups' : 'enableForFarmAndGroups'}`}
        />
      ),
      onClick: () => onFarmProtocolStatusChange(rowData, !rowData.active, true),
    },
    {
      label: <FormattedMessage id="general.button.delete" />,
      onClick: () => handleDeleteProtocol(rowData),
      hide: rowData.active,
    },
  ];

  // Terminal lifecycle statuses keep their locked compliance grade - no enable/disable for them
  const hasFinalStatus = (rowData) => ['completed', 'archived'].includes(rowData.status);

  const isCompletedNonCompliant = (rowData) => (
    rowData.status === 'completed' && rowData.compliance === 'did_not_comply'
  );

  const buildGroupOptions = (rowData) => [
    {
      label: <FormattedMessage id="general.button.edit" />,
      onClick: () => handleGroupEdit(rowData),
    },
    {
      label: <FormattedMessage id="general.button.editBarnSheet" />,
      onClick: () => router.push(`/barnsheets/groups/${rowData.pig_group.id}`),
      hide: !isCompletedNonCompliant(rowData),
    },
    {
      label: <FormattedMessage id="general.button.archiveAsNonCompliant" />,
      onClick: () => onArchiveGroupProtocol(rowData),
      hide: !isCompletedNonCompliant(rowData),
    },
    {
      label: <FormattedMessage id={`general.button.${rowData.active ? 'disable' : 'enable'}`} />,
      onClick: () => handleGroupStatusChange(rowData),
      hide: hasFinalStatus(rowData),
    },
    {
      label: <FormattedMessage id="general.button.delete" />,
      onClick: () => handleDeleteProtocol(rowData),
      hide: rowData.active,
    },
  ];

  const handleOpenDropdown = (e, rowData) => {
    const options = rowData.source_type === 'farm'
      ? buildFarmOptions(rowData).slice(1)
      : buildGroupOptions(rowData).slice(1);
    openDropdown(e, options, { isScrollIgnored: true });
  };

  const renderNameColumn = (rowData, rowIndex) => (
    <CustomColumn>
      <div className={styles['treatment-protocol-name-column']}>
        <span>
          {rowData.name}
          {rowData.combined && <CombinedBadge className="ml-10" />}
        </span>
        <i
          data-name={`${rowData.name}-${rowData.id}-${rowIndex}`}
          className={cn('fa fa-dots-three-horizontal hide-for-large', styles['dropdown-icon'])}
          onClick={(e) => handleOpenDropdown(e, rowData)}
        />
      </div>
    </CustomColumn>
  );

  const renderSourceFarmColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.sourceFarm" />}>
      {rowData.farm ? (
        <Link className="primary" to={`/treatment-protocols-farms/${rowData.farm.id}/treatment-protocols`}>
          {rowData.farm.name}
        </Link>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderSourceGroupColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.sourceGroup" />}>
      {rowData.pig_group ? (
        <Link
          className="primary"
          to={`/treatment-protocols-farms/${rowData.farm.id}/groups/${rowData.pig_group.id}/treatment-protocols`}
        >
          {rowData.pig_group.name}
        </Link>
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderMedicationsColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.medications" />}>
      {rowData.medications || <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  // Flexible Start protocols show a date range until the first administration locks the dates
  const renderStartDateColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.protocolStartDate" />}>
      {rowData.protocol_start_date
        ? formatDateRangeForUser(rowData.protocol_start_date, rowData.protocol_start_date_to, countryCode)
        : <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  const renderEndDateColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.protocolEndDate" />}>
      {rowData.protocol_end_date
        ? formatDateRangeForUser(rowData.protocol_end_date, rowData.protocol_end_date_to, countryCode)
        : <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  // Farm protocols are the templates groups inherit from; a group's own course has no effective dates
  const renderEffectiveDatesColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.effectiveDates" />}>
      {rowData.source_type === 'farm' ? (
        <EffectiveDates startDate={rowData.effective_start_date} endDate={rowData.effective_end_date} />
      ) : (
        <FormattedMessage id="general.notAvail" />
      )}
    </CustomColumn>
  );

  const renderStatusColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />}>
      <TreatmentProtocolStatusBadge status={rowData.status} />
    </CustomColumn>
  );

  // Problem grades drill into the missed-steps page, the rest into the group treatments history
  const getComplianceLinkPath = (rowData) => {
    if (!rowData.pig_group) return null;
    if (['out_of_compliance', 'did_not_comply'].includes(rowData.compliance)) {
      return `/treatment-protocols-groups/${rowData.pig_group.id}`;
    }
    return `/barnsheets/groups/${rowData.pig_group.id}/treatments`;
  };

  const renderComplianceStatusColumn = (rowData) => {
    const complianceLinkPath = getComplianceLinkPath(rowData);
    return (
      <CustomColumn label={<FormattedMessage id="general.complianceStatus" />}>
        {complianceLinkPath ? (
          <Link to={complianceLinkPath}>
            <ComplianceStatusBadge status={rowData.compliance} />
          </Link>
        ) : (
          <ComplianceStatusBadge status={rowData.compliance} />
        )}
      </CustomColumn>
    );
  };

  const renderCreatedByColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.createdBy" />}>
      {rowData.created_by || <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  const renderActionsColumn = (rowData, rowIndex) => {
    const options = rowData.source_type === 'farm'
      ? buildFarmOptions(rowData)
      : buildGroupOptions(rowData);

    const firstOption = options[0];
    const restOptions = options.slice(1);

    const statusBtnLabel = rowData.source_type === 'farm'
      ? <FormattedMessage id={`general.button.${rowData.active ? 'disableForFarm' : 'enableForFarm'}`} />
      : <FormattedMessage id={`general.button.${rowData.active ? 'disable' : 'enable'}`} />;
    const isStatusBtnVisible = rowData.source_type === 'farm' || !hasFinalStatus(rowData);

    return (
      <div className="collapsible-value button-column">
        <DropdownButton
          idKey={`${rowData.source_type}-${rowData.id}-${rowIndex}`}
          label={firstOption.label}
          buttonType="small light-gray"
          onClick={firstOption.onClick}
          dropDownData={restOptions}
          customClass="show-for-large"
          wide
        />
        <Link onClick={firstOption.onClick} className="button light hide-for-large">
          {firstOption.label}
        </Link>
        {isStatusBtnVisible && (
          <Button
            className="hide-for-large"
            onClick={() => {
              if (rowData.source_type === 'farm') {
                onFarmProtocolStatusChange(rowData, !rowData.active);
              } else {
                handleGroupStatusChange(rowData);
              }
            }}
          >
            {statusBtnLabel}
          </Button>
        )}
      </div>
    );
  };

  const columns = [
    {
      label: <FormattedMessage id="component.dataTable.headers.name" />,
      flex: '1 1 160px',
      sortKey: 'name',
      renderer: renderNameColumn,
    },
    {
      label: <FormattedMessage id="general.sourceFarm" />,
      flex: '1 1 140px',
      sortKey: 'farm_name',
      renderer: renderSourceFarmColumn,
    },
    {
      label: <FormattedMessage id="general.sourceGroup" />,
      flex: '1 1 160px',
      sortKey: 'group_name',
      renderer: renderSourceGroupColumn,
    },
    {
      label: <FormattedMessage id="general.medications" />,
      flex: '1 1 140px',
      sortKey: 'medications',
      renderer: renderMedicationsColumn,
    },
    {
      label: <FormattedMessage id="general.protocolStartDate" />,
      flex: '1 1 120px',
      sortKey: 'protocol_start_date',
      renderer: renderStartDateColumn,
    },
    {
      label: <FormattedMessage id="general.protocolEndDate" />,
      flex: '1 1 120px',
      sortKey: 'protocol_end_date',
      renderer: renderEndDateColumn,
    },
    {
      label: <FormattedMessage id="general.effectiveDates" />,
      flex: '1 1 160px',
      renderer: renderEffectiveDatesColumn,
    },
    {
      label: <FormattedMessage id="component.dataTable.headers.status" />,
      flex: '0 0 100px',
      sortKey: 'status',
      renderer: renderStatusColumn,
    },
    {
      label: <FormattedMessage id="general.complianceStatus" />,
      flex: '1 1 140px',
      sortKey: 'compliance',
      renderer: renderComplianceStatusColumn,
    },
    {
      label: <FormattedMessage id="general.createdBy" />,
      flex: '1 1 120px',
      sortKey: 'created_by',
      renderer: renderCreatedByColumn,
    },
    {
      label: '',
      flex: '0 0 135px',
      renderer: renderActionsColumn,
      fixed: true,
    },
  ];

  return (
    <section className="small-12 column FarmsSetUp">
      <Panel>
        <Panel.Heading title={<FormattedMessage id="general.treatmentProtocols" />}>
          <SearchBox initialValue={search} onChange={onSearchChange} />
        </Panel.Heading>
        <Panel.Body noPadding>
          <Preloader isActive={isLoading} />
          <TableFilter
            filters={treatmentProtocolsTableFilters}
            activeFilter={status}
            onFilterChange={onStatusChange}
            stats={stats}
            className="ph-10"
          >
            <Button className={styles['create-tp-btn']} primary onClick={handleCreateProtocol}>
              <i className="fa fa-plus mr-5" />
              <FormattedMessage id="general.createTreatmentProtocol" />
            </Button>
          </TableFilter>
          <DataTable
            data={resources}
            columns={columns}
            sort={sort || ''}
            onSortChange={onSortChange}
            scrollable
            isLoading={isLoading}
            paginationProps={paginationProps}
            rowKey="row_key"
          />
          <NothingBox
            search={search}
            itemsName="treatmentProtocols"
            display={!resources.length}
            isLoading={isLoading}
          >
            <h2 className="lighter semibold">
              <FormattedMessage id="component.nothingBox.noTreatmentProtocols" />
            </h2>
          </NothingBox>
        </Panel.Body>
      </Panel>
    </section>
  );
};

FarmsSetUp.propTypes = {
  router: T.object.isRequired,
  tableParams: T.object.isRequired,
  setPageOptions: T.func.isRequired,
  setTableParams: T.func.isRequired,
  openModal: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
  countryCode: T.string.isRequired,
};

export default connect(
  (state) => ({
    tableParams: tableParamsSelector(state, tableNames.manageTreatmentProtocols),
    countryCode: getCurrentCountryCode(state),
  }),
  {
    setPageOptions,
    setTableParams,
    openModal,
    setBreadcrumbs,
  }
)(FarmsSetUp);
